import type {ReactElement} from 'react';
import React, {useMemo, useCallback} from 'react';
import {Text, TextSize} from '@Components/text';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION, DROPDOWN_SIZE} from '@Components/dropdown-v2/dropdown.types';
import {DropdownGhostSelectBox} from '@Components/dropdown-v2/components/dropdown-ghost-select-box';
import {ControlledList} from '@Components/controlled-list';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import styles from './file-type-option.module.scss';
import type {FileTypeOptionsContentData} from '@Components/download-panel/components/file-type-option/file-type-option.types';
import {IMAGE_SORT_ORDER_FILE_TYPE_OPTIONS, VIDEO_SORT_ORDER_FILE_TYPE_OPTIONS} from '@Components/download-panel/components/file-type-option/file-type-option.types';
import {FileTypeOptions} from '@Components/download-panel/components/file-type-option/file-type-option.types';
import {useDownloadPanelData} from '@Components/download-panel/download-panel.context';
import {
  DEFAULT_IMAGE_POSTER_GIF_DURATION,
  DEFAULT_VIDEO_POSTER_GIF_DURATION,
  getRecommendedFileTypeFromPosterObject,
  isDurationEnabledForGif,
} from '@Libraries/poster-download-library';
import useWindowSize from '@Hooks/useWindowSize';
import {MOBILE_MODAL_WIDTH_THRESHOLD} from '@Components/modal';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {MODALS_IDS} from '@Components/modal-container';
import {useAppDispatch} from '@/hooks';
import {toggleInnerDropdownOpenState} from '@Components/download-panel/download-panel.reducer';
import {isPosterVideo} from '@Libraries/poster-object.library';

const DEFAULT_DOWNLOAD_FILE_TYPE = FileTypeOptions.JPG;

export function FileTypeOption(): ReactElement {
  const {fileType, setFileType, posterObject, setDuration, setSpeed} = useDownloadPanelData();
  const {windowWidth} = useWindowSize();
  const dispatch = useAppDispatch();
  const recommendedFileTypes = useMemo(() => getRecommendedFileTypeFromPosterObject(posterObject), [posterObject]);

  const FILE_TYPE_OPTIONS_DATA: FileTypeOptionsContentData[] = useMemo(
    () => [
      {
        id: FileTypeOptions.JPG,
        type: FileTypeOptions.JPG,
        icon: 'icon-image',
        text: window.i18next.t('pmwjs_jpg_image'),
        description: window.i18next.t('pmwjs_jpg_image_description'),
      },
      {
        id: FileTypeOptions.PNG,
        type: FileTypeOptions.PNG,
        icon: 'icon-image',
        text: window.i18next.t('pmwjs_png_image'),
        description: window.i18next.t('pmwjs_png_image_description_2'),
      },
      {
        id: FileTypeOptions.PDF,
        type: FileTypeOptions.PDF,
        icon: 'icon-doc',
        text: window.i18next.t('pmwjs_pdf'),
        description: window.i18next.t('pmwjs_download_pdf_description'),
      },
      {
        id: FileTypeOptions.GIF,
        type: FileTypeOptions.GIF,
        icon: 'icon-gif',
        text: window.i18next.t('pmwjs_gif'),
        description: window.i18next.t('pmwjs_gif_download_description'),
      },
      {
        id: FileTypeOptions.WEBP,
        type: FileTypeOptions.WEBP,
        icon: 'icon-image',
        text: window.i18next.t('pmwjs_webp_image'),
        description: window.i18next.t('pmwjs_webp_image_description'),
        isHidden: true,
      },
      {
        type: FileTypeOptions.WEBM,
        id: FileTypeOptions.WEBM,
        icon: 'icon-video',
        text: window.i18next.t('pmwjs_webm_video'),
        description: window.i18next.t('pmwjs_webm_video_description'),
        isHidden: true,
      },
      {
        type: FileTypeOptions.MP4,
        id: FileTypeOptions.MP4,
        icon: 'icon-video',
        text: window.i18next.t('pmwjs_mp4_video'),
        description: window.i18next.t('pmwjs_mp4_video_description_single_page'),
      },
    ],
    []
  );

  const onSelectType = useCallback(
    (id: FileTypeOptions): void => {
      if (id === FileTypeOptions.GIF) {
        if (isDurationEnabledForGif(posterObject)) {
          setDuration(DEFAULT_VIDEO_POSTER_GIF_DURATION);
        } else {
          setDuration(DEFAULT_IMAGE_POSTER_GIF_DURATION);
        }
        setSpeed(1);
      }

      setFileType(id);
      if (windowWidth < MOBILE_MODAL_WIDTH_THRESHOLD) {
        dispatch(closeModal(MODALS_IDS.MORE_OPTIONS_MODAL));
      }
    },
    [posterObject, setDuration, setSpeed, setFileType, windowWidth, dispatch]
  );

  const popupItems = useMemo((): ControlledListItemType[] => {
    const recommended: ControlledListItemType = {
      id: 'recommended',
      showDivider: false,
      type: CONTROLLED_LIST_ITEM_TYPE.HEADING,
      text: window.i18next.t('pmwjs_recommended'),
      className: 'content-ai',
      rightIconClassName: 'content-ai',
      rightIcon: 'icon-sparkle',
    };
    const more: ControlledListItemType = {
      id: 'more',
      showDivider: false,
      type: CONTROLLED_LIST_ITEM_TYPE.HEADING,
      text: window.i18next.t('pmwjs_more'),
    };
    const options = FILE_TYPE_OPTIONS_DATA.filter((data) => !data?.isHidden).map((data) => {
      return {
        id: data.type,
        className: styles.listItem,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        subText: data.description,
        isSelected: fileType === data.id,
        text: data.text,
        thumbnail: {
          type: ThumbnailType.ICON_DEFAULT,
          icon: data.icon,
        },
      };
    });

    // Get recommended options in the order specified by recommendedFileTypes
    const recommendedOptions: ControlledListItemType[] = recommendedFileTypes
      .map((recommendedType) => options.find((data) => data.id === recommendedType))
      .filter((option) => option !== undefined) as ControlledListItemType[];
    const filteredOptions = options.filter((option) => !recommendedFileTypes.includes(option.id as FileTypeOptions)) as ControlledListItemType[];

    const sortOrder = isPosterVideo(posterObject) ? VIDEO_SORT_ORDER_FILE_TYPE_OPTIONS : IMAGE_SORT_ORDER_FILE_TYPE_OPTIONS;
    const orderMap = new Map<FileTypeOptions, number>();
    sortOrder.forEach((fileType, index) => {
      orderMap.set(fileType, index);
    });
    const filteredSortedOptions = [...filteredOptions].sort((a, b) => {
      const indexA = orderMap.get(a.id as FileTypeOptions) ?? Infinity;
      const indexB = orderMap.get(b.id as FileTypeOptions) ?? Infinity;

      return indexA - indexB;
    });

    return [recommended, ...recommendedOptions, more, ...filteredSortedOptions];
  }, [fileType, FILE_TYPE_OPTIONS_DATA, recommendedFileTypes]);

  const popupContent = useMemo(
    () => (
      <div className="_full-width _fit-height overflow-auto">
        <ControlledList className="spacing-m-0" onClick={onSelectType} items={popupItems} />
      </div>
    ),
    [onSelectType, popupItems]
  );

  const dropdownSelector = useMemo(() => {
    const data = FILE_TYPE_OPTIONS_DATA.find((data) => data.type === (fileType ?? DEFAULT_DOWNLOAD_FILE_TYPE));

    if (!data) {
      throw new Error('File type data not found');
    }

    return <DropdownGhostSelectBox iconClassName={styles.iconColor} icon={data.icon} size={DROPDOWN_SIZE.SMALL} text={data.text} />;
  }, [FILE_TYPE_OPTIONS_DATA, fileType]);

  const toggleStateInRedux = (state: boolean): void => {
    dispatch(toggleInnerDropdownOpenState(state));
  };

  return (
    <div className="_full-width flex-h-row justify-content-between flex-items-center">
      <Text bold size={TextSize.XSMALL} val={window.i18next.t('pmwjs_file_type')} />
      <Dropdown
        onOpen={(): void => {
          toggleStateInRedux(true);
        }}
        onClose={(): void => {
          toggleStateInRedux(false);
        }}
        popupSpacingClass="spacing-p-t-3 spacing-p-b-4 spacing-p-r-4 spacing-p-l-4"
        position={DROPDOWN_POSITION.FIXED}
        popupClassName={styles.popup}
        alignment={ALIGNMENT_TYPE.BOTTOM_END}
        selector={dropdownSelector}
        popup={popupContent}
        popUpHasCustomHeight
        showModalForSmallScreen
        maxHeight={400}
        modalTitle={window.i18next.t('pmwjs_file_type')}
      />
    </div>
  );
}
